import * as React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import TodayIcon from '@mui/icons-material/Today';
import Copyright from '../internals/components/Copyright';
import SensorOverview from './SensorOverview';
import SensorStatusBar from './SensorStatusBar';
import GrowProgressBar from './GrowProgressBar';
import { SensorViewProvider } from '../context/SensorViewContext';
import { SensorConfigProvider } from '../context/SensorConfigContext';
import { useGrow, GROW_STAGES } from '../context/GrowContext';

const STAGE_ORDER = ['seedling', 'vegetative', 'flower', 'lateFlower'];

// Helper to get today's date in YYYY-MM-DD format
const getTodayString = () => new Date().toISOString().split('T')[0];

// Start Grow Dialog Component
function StartGrowDialog({ open, onClose, onStart, presets }) {
  const [growName, setGrowName] = React.useState('');
  const [selectedPreset, setSelectedPreset] = React.useState(presets[0]?.id || '');
  const [startDate, setStartDate] = React.useState(getTodayString());
  const [startStage, setStartStage] = React.useState('seedling');

  React.useEffect(() => {
    if (open) {
      setGrowName('');
      setStartDate(getTodayString());
      setStartStage('seedling');
      if (presets.length > 0) {
        setSelectedPreset(presets[0].id);
      }
    }
  }, [open, presets]);

  const handleStart = () => {
    if (selectedPreset) {
      const name = growName.trim() || `Grow ${new Date().toLocaleDateString()}`;
      onStart(name, selectedPreset, new Date(startDate + 'T00:00:00'), startStage);
      onClose();
    }
  };

  const handleSetToday = () => {
    setStartDate(getTodayString());
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>
        Start New Grow
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            label="Grow Name"
            value={growName}
            onChange={(e) => setGrowName(e.target.value)}
            placeholder="e.g., Spring Batch 2025"
            size="small"
            fullWidth
            autoFocus
          />

          <FormControl fullWidth size="small">
            <InputLabel>Select Preset</InputLabel>
            <Select
              value={selectedPreset}
              onChange={(e) => setSelectedPreset(e.target.value)}
              label="Select Preset"
            >
              {presets.map((preset) => (
                <MenuItem key={preset.id} value={preset.id}>
                  {preset.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <Stack direction="row" spacing={1} alignItems="flex-end">
              <TextField
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <Tooltip title="Set to today">
                <IconButton
                  onClick={handleSetToday}
                  size="small"
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'primary.dark' },
                    mb: 0.5,
                  }}
                >
                  <TodayIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>

          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
              Starting Stage
            </Typography>
            <ToggleButtonGroup
              value={startStage}
              exclusive
              onChange={(_, v) => v && setStartStage(v)}
              size="small"
              fullWidth
            >
              {STAGE_ORDER.map((stage) => (
                <ToggleButton
                  key={stage}
                  value={stage}
                  sx={{ textTransform: 'none', fontSize: '0.7rem', py: 0.5 }}
                >
                  {GROW_STAGES[stage].order}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
              {GROW_STAGES[startStage].label}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleStart} variant="contained" startIcon={<PlayArrowIcon />}>
          Start Grow
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Individual Grow Card with progress and jump button
function GrowCard({ grow, onNavigate }) {
  const { getGrowProgress, getPresetForGrow, setActiveGrowId } = useGrow();

  const progress = getGrowProgress(grow.id);
  const preset = getPresetForGrow(grow.id);

  const handleNavigate = () => {
    setActiveGrowId(grow.id);
    onNavigate(grow.id);
  };

  if (!progress) return null;

  return (
    <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack spacing={2}>
        {/* Header with name and jump button */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
              }}
            >
              {grow.name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {preset?.name || 'Unknown Preset'} • Started {new Date(grow.startDate).toLocaleDateString()}
            </Typography>
          </Box>
          <Tooltip title="Go to grow dashboard">
            <IconButton
              onClick={handleNavigate}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': { bgcolor: 'primary.dark' },
              }}
            >
              <OpenInNewIcon />
            </IconButton>
          </Tooltip>
        </Stack>

        {/* Progress Bar */}
        <GrowProgressBar growId={grow.id} compact />
      </Stack>
    </Card>
  );
}

export default function MainGrid() {
  const navigate = useNavigate();
  const location = useLocation();
  const { grows, presets, addGrow } = useGrow();

  const [showStartDialog, setShowStartDialog] = React.useState(false);

  // Determine if we're in demo mode based on the URL
  const isDemo = location.pathname.startsWith('/demo');
  const basePath = isDemo ? '/demo' : '/app';

  // Check for ?new=true query param to auto-open dialog
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('new') === 'true') {
      setShowStartDialog(true);
      // Remove the query param
      navigate(basePath, { replace: true });
    }
  }, [location.search, navigate, basePath]);

  const handleStartGrow = (name, presetId, startDate, startStage) => {
    const growId = addGrow(name, presetId, startDate, startStage);
    navigate(`${basePath}/grow/${growId}`);
  };

  const handleNavigateToGrow = (growId) => {
    navigate(`${basePath}/grow/${growId}`);
  };

  return (
    <SensorConfigProvider>
      <SensorViewProvider>
        <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
          {/* Page Header */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Typography
              variant="h5"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
              }}
            >
              Dashboard
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowStartDialog(true)}
            >
              New Grow
            </Button>
          </Stack>

          {/* Active Grows Section */}
          {grows.length > 0 ? (
            <>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  mb: 2,
                }}
              >
                Active Grows ({grows.filter(g => g.status === 'active').length})
              </Typography>

              {/* Grow Cards - 2 per row */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
                {grows.filter(g => g.status === 'active').map((grow) => (
                  <Box key={grow.id} sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 300 }}>
                    <GrowCard grow={grow} onNavigate={handleNavigateToGrow} />
                  </Box>
                ))}
              </Box>

              {/* Sensor Overview Section */}
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  mb: 2,
                }}
              >
                Sensor Overview
              </Typography>
              <SensorOverview />

              {/* Current Readings */}
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  mb: 2,
                  mt: 4,
                }}
              >
                Current Readings
              </Typography>
              <Box sx={{ mb: 4 }}>
                <SensorStatusBar />
              </Box>
            </>
          ) : (
            /* No grows - show empty state */
            <Card
              variant="outlined"
              sx={{
                p: 6,
                textAlign: 'center',
                bgcolor: 'action.hover',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  color: 'text.secondary',
                  mb: 2,
                }}
              >
                No active grows
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  mb: 3,
                }}
              >
                Start your first grow to begin tracking sensor data and progress.
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={<PlayArrowIcon />}
                onClick={() => setShowStartDialog(true)}
              >
                Start Your First Grow
              </Button>
            </Card>
          )}

          <Copyright sx={{ my: 4 }} />
        </Box>

        <StartGrowDialog
          open={showStartDialog}
          onClose={() => setShowStartDialog(false)}
          onStart={handleStartGrow}
          presets={presets}
        />
      </SensorViewProvider>
    </SensorConfigProvider>
  );
}
